import { useState } from 'react';

import type { VatSuspenseRow } from '@dcc-erp/shared';
import { fmt } from '@dcc-erp/shared';

import { useRequestVatWriteOff } from '../../lib/queries/vat-writeoff';
import { AppModal } from '../AppModal';

// =============================================================================
// Request a deferred-VAT write-off (ตัดจำหน่าย VAT) for an OVERDUE suspense bill.
//
// Shows the bill voucher + suspense VAT amount read-only (the write-off amount
// is the suspense VAT, not editable), a writeOffDate (default today) + reason.
// Submit POSTs a DRAFT VatWriteOffRequest via useRequestVatWriteOff — the
// reversing JV (Dr expense / Cr VAT_INPUT_SUSPENSE) is booked on final engine
// approval, not here. The trigger button is gated to OVERDUE rows upstream
// (B2/B5); this modal only owns the form. Plain prototype.css — no tailwind.
// =============================================================================

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface RequestWriteOffModalProps {
  companyId: string | undefined;
  row: VatSuspenseRow;
  onClose: () => void;
}

export function RequestWriteOffModal({ companyId, row, onClose }: RequestWriteOffModalProps) {
  const request = useRequestVatWriteOff(companyId);
  const [writeOffDate, setWriteOffDate] = useState(today());
  const [reason, setReason] = useState('');

  const busy = request.isPending;
  const error = request.error;

  function handleSubmit() {
    void (async () => {
      await request.mutateAsync({
        apBillId: row.apBillId,
        writeOffDate,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      onClose();
    })();
  }

  return (
    <AppModal
      title="ตัดจำหน่าย VAT ซื้อ (เกินกำหนดเคลม)"
      subtitle={`${row.voucherNo} · ${row.vendorName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={handleSubmit}>
            {busy ? 'กำลังบันทึก…' : 'ตัดจำหน่าย'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="meta">
          ใบสำคัญ <b className="code">{row.voucherNo}</b> · VAT พักรอเคลม{' '}
          <b className="tnum">{fmt(row.vatAmount, 2)}</b> บาท จะถูกตัดเป็นค่าใช้จ่าย (Dr ค่าใช้จ่าย
          VAT เกินกำหนด / Cr VAT พักรอเคลม) เมื่ออนุมัติครบขั้น
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
              ใบสำคัญ
            </label>
            <input
              className="input"
              type="text"
              aria-label="ใบสำคัญ"
              value={row.voucherNo}
              readOnly
              style={{ width: '100%', height: 38 }}
            />
          </div>
          <div>
            <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
              ยอด VAT พักรอเคลม
            </label>
            <input
              className="input tnum"
              type="text"
              aria-label="ยอด VAT พักรอเคลม"
              value={fmt(row.vatAmount, 2)}
              readOnly
              style={{ width: '100%', height: 38, textAlign: 'right' }}
            />
          </div>
        </div>

        <div>
          <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
            วันที่ตัดจำหน่าย
          </label>
          <input
            className="input"
            type="date"
            aria-label="วันที่ตัดจำหน่าย"
            value={writeOffDate}
            onChange={(e) => setWriteOffDate(e.target.value)}
            style={{ width: '100%', height: 38 }}
          />
        </div>

        <div>
          <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
            เหตุผล
          </label>
          <textarea
            className="input"
            aria-label="เหตุผล"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="เช่น ใบกำกับภาษีเกิน 6 เดือน เคลมไม่ได้ตามมาตรา 82/5"
            style={{ width: '100%', padding: 10, resize: 'vertical' }}
          />
        </div>

        {error && (
          <div className="badge negative" style={{ fontSize: 13 }}>
            บันทึกไม่สำเร็จ: {error.message}
          </div>
        )}
      </div>
    </AppModal>
  );
}
